/**
 * section-command-service.js — the adapter between a command-bar pane and
 * `sections/section-manager.js`.
 *
 * The panes do not call the manager directly, because the manager's API is not
 * the API a toolbar wants and the mismatch is not cosmetic:
 *
 *  1. `moveSection(instanceId, newIndex, pageId)` takes an ABSOLUTE INSERTION
 *     INDEX and adjusts internally for the removal of the source. A toolbar has
 *     an up/down button. Getting that arithmetic wrong at each call site is how
 *     "move down" silently becomes "move down two" at the end of a list.
 *  2. `removeSection` and `bulkDeleteSections` CONFIRM unless
 *     `skipConfirm: true`. Row 2's contract is act-then-undo-toast, so every
 *     delete from the bar must pass that flag — deliberately, in ONE place,
 *     rather than as a habit spread across panes.
 *  3. `removeSharedSection(sharedType)` confirms by default. Row 2 passes the
 *     same explicit `skipConfirm: true` policy as every other destructive verb.
 *  4. Peer locks and read-only tabs must DISABLE a control before it is
 *     clicked. The manager checks and bails, which is a correct backstop but a
 *     poor affordance: the user clicks and nothing happens.
 *
 * So this module owns direction→index arithmetic, the confirmation policy, the
 * shared-section split, and the capability computation that drives disabled
 * state and its copy.
 *
 * ── BC-8: the ONE destructive policy, for every surface ───────────────────
 * It used to be row 2's policy only. The floating toolbar, the property ⋮
 * menu, Delete/Backspace, the command palette and the section list called the
 * managers directly and got their confirm-by-default, so the same delete
 * confirmed or not depending on where you clicked. Every surface now comes
 * here (`destructive-policy-tests.js` fails on a direct call), and the policy
 * is builder-coherence contracts §3: section delete, reset, shared removal,
 * bulk delete and page delete ACT and offer Undo. A surface that is not a
 * pane — a key press, a palette command, a list row — passes
 * `{announce: true}` so a refusal explains itself instead of doing nothing.
 *
 * ── Capabilities are computed, never assumed ──────────────────────────────
 * `sectionCapabilities()` returns a reason string per action, or null when the
 * action is available. The string is user-facing and goes into `aria-label`,
 * because a disabled button does not reliably receive hover or focus — the same
 * reason `_syncUndoRedoChrome` in today's toolbar swaps its label rather than
 * relying on a tooltip.
 */

import { store, readOnlyReasonFor } from '../../core/state.js';
import { sharedTypeOf } from '../../shared/section-order.js';
import {
  isSectionLockedByPeer,
  lockKeyForInstance,
  lockKeyForShared
} from '../../editing/collab-guard.js';
import {
  moveSection,
  duplicateSection,
  removeSection,
  removeSharedSection,
  resetSection,
  bulkDeleteSections,
  bulkDuplicateSections,
  bulkMoveSections
} from '../../sections/section-manager.js';
import { selection } from '../../core/selection-store.js';
import { removePage as removePageFromManager } from '../../pages/page-manager.js';
import { showToast } from '../toast.js';

/** Actions a section pane can offer. Order is the render order in row 2. */
export const SECTION_ACTIONS = Object.freeze([
  'replace',
  'background',
  'spacing',
  'visibility',
  'move-up',
  'move-down',
  'duplicate',
  'delete',
  'reset',
  'all-settings'
]);

/** Shared generic section verbs, in context-menu order. */
export const SECTION_VERBS = Object.freeze([
  { id: 'duplicate', label: 'Duplicate', run: duplicate },
  { id: 'move-up', label: 'Move up', run: (id) => moveByDirection(id, -1) },
  { id: 'move-down', label: 'Move down', run: (id) => moveByDirection(id, 1) },
  { id: 'reset', label: 'Reset section', run: reset },
  { id: 'delete', label: 'Delete', danger: true, run: remove }
]);

/**
 * The page order a section actually lives in.
 *
 * NOT `pageSectionOrder(state)`: that helper takes ONE argument and always
 * resolves the ACTIVE page. Passing it a pageId is silently ignored — which
 * would make every capability on a non-active page (index, first/last, count)
 * quietly wrong rather than loudly wrong. Row 2 only ever acts on the active
 * page today, but a helper that accepts an argument it discards is a trap for
 * whoever adds the second caller.
 *
 * @returns {string[]} instance ids in order; empty when the page is unknown.
 */
function orderForSection(state, instanceId, pageId) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  const targetId = pageId || state?.activePage;
  let page = pages.find((p) => p?.id === targetId) || null;
  // No explicit page and the section is not on the active one — find its home
  // rather than reporting it as missing.
  if (!page || !page.sections?.some((s) => s?.instanceId === instanceId)) {
    page = pages.find((p) => p?.sections?.some((s) => s?.instanceId === instanceId)) || page;
  }
  if (!page || !Array.isArray(page.sections)) return [];
  return page.sections.filter((s) => s?.instanceId).map((s) => s.instanceId);
}

/** Actions a SHARED section (nav/footer) cannot offer at all. */
const SHARED_UNAVAILABLE = Object.freeze({
  // Shared sections are not members of the page order; there is nothing to
  // reorder them against. The manager has no code path for it either.
  'move-up': 'The navigation and footer sit outside the page order',
  'move-down': 'The navigation and footer sit outside the page order',
  // Duplicating a shared section would produce a second nav on every page.
  duplicate: 'A shared section can only exist once',
  reset: 'Reset is unavailable for the shared navigation and footer.'
});

/**
 * Everything a pane needs to render the section scope, computed from settled
 * state. Pure with respect to the store — it reads, never writes.
 *
 * @param {string} instanceId
 * @param {{pageId?: string}} [opts]
 * @returns {{
 *   instanceId: string, sharedType: 'navigation'|'footer'|null,
 *   index: number, count: number, locked: boolean, writable: boolean,
 *   disabledReason: Record<string, string|null>
 * }|null} null when the id resolves to nothing.
 */
export function sectionCapabilities(instanceId, opts = {}) {
  if (!instanceId) return null;
  const state = store.getState();
  if (!state) return null;

  const sharedType = sharedTypeOf(state, instanceId);
  const order = orderForSection(state, instanceId, opts.pageId);
  const index = sharedType ? -1 : order.indexOf(instanceId);
  if (!sharedType && index === -1) return null;

  const lockKey = sharedType ? lockKeyForShared(sharedType) : lockKeyForInstance(instanceId);
  const locked = isSectionLockedByPeer(lockKey);
  const writable = typeof store.isWritable === 'function' ? store.isWritable() : true;

  const disabledReason = {};
  for (const action of SECTION_ACTIONS) {
    // Precedence matters and is deliberate: read-only outranks a peer lock,
    // because in a read-only tab NOTHING is editable and naming a specific
    // peer would imply that resolving the lock would help.
    if (!writable) {
      // BC-1: the sentence for the blocker actually held. "Edit in the other
      // tab" was wrong for every blocker but the tab guard — previewing an old
      // version, an expired session, a deleted project.
      disabledReason[action] = readOnlyReasonFor(store.getPrimaryBlocker());
      continue;
    }
    if (locked) {
      disabledReason[action] = 'Someone else is editing this section';
      continue;
    }
    if (sharedType && SHARED_UNAVAILABLE[action]) {
      disabledReason[action] = SHARED_UNAVAILABLE[action];
      continue;
    }
    if (action === 'move-up' && index === 0) {
      disabledReason[action] = 'Already first';
      continue;
    }
    if (action === 'move-down' && index === order.length - 1) {
      disabledReason[action] = 'Already last';
      continue;
    }
    disabledReason[action] = null;
  }

  return {
    instanceId,
    sharedType,
    index,
    count: order.length,
    locked,
    writable,
    disabledReason
  };
}

/** True when `action` is offerable for this section right now. */
export function canRun(instanceId, action, opts = {}) {
  const caps = sectionCapabilities(instanceId, opts);
  const ok = Boolean(caps && caps.disabledReason[action] === null);
  if (!ok && opts.announce) announceRefusal(caps?.disabledReason?.[action] || null);
  return ok;
}

/**
 * A surface with no disabled state to show (a key press, a palette command)
 * gets the same reason a pane would have shown on its button. Read-only goes
 * through `store:write-rejected`, so the shell's one listener names the
 * blocker and it is never toasted twice.
 */
function announceRefusal(reason) {
  if (!store.isWritable()) {
    store.refuseIfReadOnly('Section action');
    return;
  }
  if (reason) showToast(reason, { type: 'info', id: 'section-action-refused' });
}

/**
 * Translate an up/down gesture into the absolute insertion index
 * `moveSection` expects.
 *
 * `moveSection` decrements the target when moving forward, because it splices
 * the source out first. So "one step down" is `index + 2`, not `index + 1` —
 * passing `index + 1` is a no-op, which is exactly the bug that makes a move
 * button look broken. Returns null when the move is not possible.
 *
 * @param {string[]} order — page section order
 * @param {string} instanceId
 * @param {-1|1} direction
 * @returns {number|null}
 */
export function insertionIndexForDirection(order, instanceId, direction) {
  const index = order.indexOf(instanceId);
  if (index === -1) return null;
  if (direction < 0) return index === 0 ? null : index - 1;
  if (index >= order.length - 1) return null;
  return index + 2;
}

// ── verbs ───────────────────────────────────────────────────────────────────

/**
 * @param {-1|1} direction
 * @returns {Promise<boolean>} whether the move ran
 */
export async function moveByDirection(instanceId, direction, opts = {}) {
  if (!canRun(instanceId, direction < 0 ? 'move-up' : 'move-down', opts)) return false;
  const order = orderForSection(store.getState(), instanceId, opts.pageId);
  const target = insertionIndexForDirection(order, instanceId, direction);
  if (target === null) return false;
  await moveSection(instanceId, target, opts.pageId);
  return true;
}

/** @returns {Promise<boolean>} */
export async function duplicate(instanceId, opts = {}) {
  if (!canRun(instanceId, 'duplicate', opts)) return false;
  await duplicateSection(instanceId, opts.pageId);
  return true;
}

/**
 * Delete, act-then-undo.
 *
 * Page sections pass `skipConfirm: true` — row 2's contract is that the undo
 * toast IS the confirmation, and the manager already emits one.
 *
 * Shared navigation/footer use the same explicit policy. Other callers retain
 * the manager's confirmation default; only row 2 opts out.
 *
 * @returns {Promise<{ran: boolean, confirmed: false}>}
 */
export async function remove(instanceId, opts = {}) {
  if (!canRun(instanceId, 'delete', opts)) return { ran: false, confirmed: false };
  const sharedType = sharedTypeOf(store.getState(), instanceId);
  if (sharedType) {
    const ran = await removeSharedSection(sharedType, { skipConfirm: true });
    return { ran: Boolean(ran), confirmed: false };
  }
  const ran = await removeSection(instanceId, opts.pageId, { skipConfirm: true });
  return { ran: Boolean(ran), confirmed: false };
}

/**
 * Reset to the section's stock content. Same act-then-undo contract as delete.
 * @returns {Promise<boolean>}
 */
export async function reset(instanceId, opts = {}) {
  if (!canRun(instanceId, 'reset', opts)) return false;
  const ran = await resetSection(instanceId, opts.pageId, { skipConfirm: true });
  return Boolean(ran);
}

// ── multi-selection ─────────────────────────────────────────────────────────
//
// The bulk verbs row 2 offers when more than one section is selected. They exist
// HERE rather than in the pane for the same reason the single-section ones do:
// `bulkDeleteSections` confirms by DEFAULT, and `skipConfirm: true` is a policy
// decision that must live in one place. A pane that forgot it would put a
// confirm dialog in row 2, which contracts §9 forbids outright.
//
// The section list's bulk bar reaches the managers directly and keeps its own
// confirm behaviour — converging the two surfaces is deliberately out of scope,
// because a bar and a list are allowed to make different promises about a
// dialog. What is NOT allowed is row 2 making that decision per button.

/**
 * What the multi pane may offer, and why not.
 *
 * A read-only tab blocks everything. A peer-locked member blocks the whole
 * operation rather than silently acting on the rest — a bulk verb that quietly
 * skips one of your four selected sections is worse than one that refuses.
 *
 * @returns {{count: number, writable: boolean, disabledReason: Record<string,string|null>}}
 */
export function multiCapabilities(opts = {}) {
  const ids = Array.isArray(opts.instanceIds) ? opts.instanceIds : selection.getIds();
  const state = store.getState();
  const writable = typeof store.isWritable === 'function' ? store.isWritable() : true;

  let reason = null;
  if (!writable) reason = readOnlyReasonFor(store.getPrimaryBlocker());
  else if (ids.length < 2) reason = 'Select more than one section';
  else if (ids.some((id) => isSectionLockedByPeer(lockKeyForInstance(id)))) {
    reason = 'Someone else is editing one of these sections';
  } else if (ids.some((id) => sharedTypeOf(state, id))) {
    // Shared nav/footer are not members of the page order, so a bulk move has
    // nothing to move them against and a bulk duplicate would put a second nav
    // on every page.
    reason = 'The navigation and footer cannot be changed in bulk';
  }

  return {
    count: ids.length,
    writable,
    disabledReason: {
      'move-up': reason,
      'move-down': reason,
      duplicate: reason,
      delete: reason
    }
  };
}

const canRunBulk = (action, opts) => {
  const reason = multiCapabilities(opts).disabledReason[action];
  if (reason !== null && opts?.announce) announceRefusal(reason);
  return reason === null;
};

/** @returns {Promise<boolean>} */
export async function bulkMove(direction, opts = {}) {
  const action = direction === 'up' ? 'move-up' : 'move-down';
  if (!canRunBulk(action, opts)) return false;
  const ids = Array.isArray(opts.instanceIds) ? opts.instanceIds : selection.getIds();
  return Boolean(await bulkMoveSections(ids, { pageId: opts.pageId, direction }));
}

/** @returns {Promise<boolean>} */
export async function bulkDuplicate(opts = {}) {
  if (!canRunBulk('duplicate', opts)) return false;
  const ids = Array.isArray(opts.instanceIds) ? opts.instanceIds : selection.getIds();
  return Boolean(await bulkDuplicateSections(ids, { pageId: opts.pageId }));
}

/**
 * Act-then-undo, like every other destructive verb in row 2.
 * `skipConfirm: true` is passed HERE and nowhere else.
 * @returns {Promise<boolean>}
 */
export async function bulkRemove(opts = {}) {
  if (!canRunBulk('delete', opts)) return false;
  const ids = Array.isArray(opts.instanceIds) ? opts.instanceIds : selection.getIds();
  return Boolean(
    await bulkDeleteSections(ids, {
      pageId: opts.pageId,
      skipConfirm: true,
      hiddenNames: Array.isArray(opts.hiddenNames) ? opts.hiddenNames : []
    })
  );
}

/**
 * BC-8. Page delete, act-then-undo like every other destructive verb. The page
 * manager records one compound history entry and shows the Undo toast; a
 * refusal (the last page, a read-only tab) explains itself from the manager.
 * @returns {Promise<boolean>}
 */
export async function removePage(pageId) {
  if (!pageId) return false;
  return Boolean(await removePageFromManager(pageId));
}
